/**
 * Environment3D.jsx — Phase 1 Roller-Coaster Park Environment
 *
 * Design goals:
 *  • Rich daytime scene — no "block game" look
 *  • PBR materials with metalness/roughness
 *  • Dramatic sky, layered mountains, dense treelines
 *  • Spectator stands, control tower, research buildings
 *  • Track support pylons, bridges, safety barriers
 *  • Warm afternoon lighting with shadows
 */
import * as THREE from "three";

// ── Reusable primitives ────────────────────────────────────────────
const Box = ({ pos, size, color, metalness = 0, roughness = 0.85, emissive, emissiveIntensity = 0.2, cast = true, receive = true }) => (
  <mesh position={pos} castShadow={cast} receiveShadow={receive}>
    <boxGeometry args={size} />
    <meshStandardMaterial color={color} metalness={metalness} roughness={roughness}
      emissive={emissive ?? color} emissiveIntensity={emissive ? emissiveIntensity : 0} />
  </mesh>
);

const Cyl = ({ pos, args, color, metalness = 0, roughness = 0.85, rot = [0, 0, 0] }) => (
  <mesh position={pos} rotation={rot} castShadow receiveShadow>
    <cylinderGeometry args={args} />
    <meshStandardMaterial color={color} metalness={metalness} roughness={roughness} />
  </mesh>
);

// ── Pine tree ─────────────────────────────────────────────────────
function Tree({ pos, scale = 1, hue = 0 }) {
  const trunkH = 3.5 * scale;
  const colors = ["#2d6b2d", "#3a8a3a", "#226622", "#1e5c1e", "#4a9a4a"];
  const c = colors[hue % colors.length];
  return (
    <group position={pos}>
      <Cyl pos={[0, trunkH / 2, 0]} args={[0.3 * scale, 0.45 * scale, trunkH, 7]} color="#6b4c2a" roughness={0.95} />
      <mesh castShadow position={[0, trunkH + 2.5 * scale, 0]}>
        <coneGeometry args={[2.8 * scale, 5.5 * scale, 7]} />
        <meshStandardMaterial color={c} roughness={0.88} />
      </mesh>
      <mesh castShadow position={[0, trunkH + 5 * scale, 0]}>
        <coneGeometry args={[1.8 * scale, 3.5 * scale, 7]} />
        <meshStandardMaterial color={c} roughness={0.88} />
      </mesh>
    </group>
  );
}

// ── Deciduous tree ─────────────────────────────────────────────────
function RoundTree({ pos, scale = 1, color = "#3a7d44" }) {
  return (
    <group position={pos}>
      <Cyl pos={[0, 2.5 * scale, 0]} args={[0.28 * scale, 0.38 * scale, 5 * scale, 7]} color="#7a5230" roughness={0.95} />
      <mesh castShadow position={[0, 7 * scale, 0]}>
        <sphereGeometry args={[2.8 * scale, 10, 8]} />
        <meshStandardMaterial color={color} roughness={0.85} />
      </mesh>
    </group>
  );
}

// ── Building ───────────────────────────────────────────────────────
function Building({ pos, size = [12, 14, 10], wallColor = "#e8d5b0", roofColor = "#c0392b" }) {
  const [w, h, d] = size;
  return (
    <group position={pos}>
      <Box pos={[0, h / 2, 0]} size={size} color={wallColor} roughness={0.88} />
      {/* Roof */}
      <mesh castShadow position={[0, h + 1.5, 0]}>
        <coneGeometry args={[Math.max(w, d) * 0.8, 3, 4]} />
        <meshStandardMaterial color={roofColor} roughness={0.75} />
      </mesh>
      {/* Windows */}
      {[-w / 4, w / 4].map((x, i) =>
        [h * 0.3, h * 0.6].map((y, j) => (
          <Box key={`${i}${j}`} pos={[x, y, d / 2 + 0.05]} size={[w / 6, h / 8, 0.12]}
            color="#7dd3fc" metalness={0.9} roughness={0.05} emissive="#bae6fd" emissiveIntensity={0.15} />
        ))
      )}
      {/* Door */}
      <Box pos={[0, h * 0.1, d / 2 + 0.05]} size={[w / 8, h * 0.22, 0.12]} color="#5a3a20" roughness={0.95} />
    </group>
  );
}

// ── Control tower ──────────────────────────────────────────────────
function ControlTower({ pos }) {
  return (
    <group position={pos}>
      <Cyl pos={[0, 12, 0]} args={[1.2, 1.5, 24, 8]} color="#94a3b8" metalness={0.55} roughness={0.4} />
      {/* Observation deck */}
      <Box pos={[0, 26, 0]} size={[7, 4, 7]} color="#1e3a5f" metalness={0.3} roughness={0.5} />
      {/* Glass ring */}
      <Box pos={[0, 27.5, 0]} size={[6.5, 2, 6.5]} color="#7dd3fc" metalness={0.95} roughness={0.05}
        emissive="#bae6fd" emissiveIntensity={0.25} />
      {/* Antenna */}
      <Cyl pos={[0, 32, 0]} args={[0.08, 0.06, 5, 5]} color="#64748b" metalness={0.8} roughness={0.2} />
      {/* Red beacon */}
      <mesh position={[0, 35.5, 0]}>
        <sphereGeometry args={[0.35, 8, 8]} />
        <meshStandardMaterial color="#ef4444" emissive="#dc2626" emissiveIntensity={3.0} />
      </mesh>
    </group>
  );
}

// ── Spectator stand ────────────────────────────────────────────────
function Stand({ pos, rot = [0, 0, 0] }) {
  return (
    <group position={pos} rotation={rot}>
      {/* Tiers */}
      {[0, 1, 2, 3].map((tier) => (
        <Box key={tier} pos={[0, tier * 2.2 + 1.1, tier * 1.8]}
          size={[22, 2.1, 3.5]}
          color={tier % 2 === 0 ? "#dc2626" : "#1d4ed8"}
          roughness={0.78} />
      ))}
      {/* Support columns */}
      {[-9, -3, 3, 9].map((x, i) => (
        <Cyl key={i} pos={[x, 3, 0]} args={[0.35, 0.35, 9, 6]} color="#374151" roughness={0.85} />
      ))}
      {/* Roof */}
      <Box pos={[0, 10, -1]} size={[24, 0.5, 4.5]} color="#1f2937" roughness={0.9} />
    </group>
  );
}

// ── Track support pylon ────────────────────────────────────────────
function Pylon({ pos, height = 8 }) {
  return (
    <group position={pos}>
      <Cyl pos={[0, height / 2, 0]} args={[0.45, 0.6, height, 8]} color="#64748b" metalness={0.5} roughness={0.4} />
      {/* Cap */}
      <Box pos={[0, height + 0.4, 0]} size={[2.8, 0.6, 0.6]} color="#94a3b8" metalness={0.6} roughness={0.3} />
    </group>
  );
}

// ── Mountain ───────────────────────────────────────────────────────
function Mountain({ pos, h, r, snowLine = 0.7 }) {
  return (
    <group position={pos}>
      <mesh castShadow>
        <coneGeometry args={[r, h, 5]} />
        <meshStandardMaterial color="#7c6f64" roughness={0.97} />
      </mesh>
      {/* Snow cap */}
      <mesh position={[0, h * snowLine - h / 2, 0]}>
        <coneGeometry args={[r * (1 - snowLine), h * (1 - snowLine), 5]} />
        <meshStandardMaterial color="#f0f4f8" roughness={0.7} />
      </mesh>
    </group>
  );
}

// ── Cloud ─────────────────────────────────────────────────────────
function Cloud({ pos }) {
  return (
    <group position={pos}>
      {[
        [0, 0, 0, 5],
        [6, 1, 2, 4],
        [-5, -0.5, 1, 3.5],
        [3, -1, -2, 3],
      ].map(([x, y, z, r], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[r, 8, 6]} />
          <meshStandardMaterial color="#f0f6ff" roughness={1} metalness={0} />
        </mesh>
      ))}
    </group>
  );
}

// ── Safety barrier fence ──────────────────────────────────────────
function Barrier({ from, to }) {
  const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2 + 1.5, (from[2] + to[2]) / 2];
  const dx = to[0] - from[0], dz = to[2] - from[2];
  const len = Math.sqrt(dx * dx + dz * dz);
  const angle = Math.atan2(dx, dz);
  return (
    <group position={mid} rotation={[0, angle, 0]}>
      <Box pos={[0, 0, 0]} size={[len, 0.15, 0.15]} color="#fbbf24" metalness={0.3} roughness={0.6} />
      {Array.from({ length: Math.floor(len / 5) }, (_, i) => (
        <Cyl key={i} pos={[0, -0.8, -len / 2 + (i + 0.5) * (len / Math.floor(len / 5))]}
          args={[0.08, 0.08, 1.6, 5]} color="#374151" roughness={0.9}
          rot={[Math.PI / 2, 0, 0]} />
      ))}
    </group>
  );
}

// ── Distance board ────────────────────────────────────────────────
function DistBoard({ pos, label, color = "#22c55e" }) {
  return (
    <group position={pos}>
      <Cyl pos={[0, 4, 0]} args={[0.15, 0.15, 8, 6]} color="#374151" roughness={0.9} />
      <Box pos={[0, 8.5, 0]} size={[4, 1.5, 0.2]} color="#1e3a5f" roughness={0.7} />
      <Box pos={[0, 8.5, 0.12]} size={[3.5, 1.1, 0.05]} color={color} metalness={0.1} roughness={0.4}
        emissive={color} emissiveIntensity={0.4} />
    </group>
  );
}

// ── Main Environment ───────────────────────────────────────────────
// New track spans: x ∈ [-265, 295], z ∈ [-205, 112]
// World centre ≈ [15, 0, -47]. Ground plane 1200×1200 covers everything.
export default function Environment3D() {
  return (
    <group>
      {/* ── Lighting ── */}
      <ambientLight intensity={0.5} color="#fff5e4" />
      <directionalLight
        position={[200, 350, 100]} intensity={2.2} color="#fffbe8"
        castShadow
        shadow-mapSize-width={4096}
        shadow-mapSize-height={4096}
        shadow-camera-near={1}
        shadow-camera-far={1800}
        shadow-camera-left={-600}
        shadow-camera-right={600}
        shadow-camera-top={500}
        shadow-camera-bottom={-500}
        shadow-bias={-0.0005}
      />
      <directionalLight position={[-150, 80, 150]} intensity={0.35} color="#bfdbfe" />
      <hemisphereLight skyColor="#87ceeb" groundColor="#3a7d44" intensity={0.45} />

      {/* ── Sky ── */}
      <mesh>
        <sphereGeometry args={[1000, 32, 16]} />
        <meshStandardMaterial color="#5ba8d4" side={THREE.BackSide} roughness={1} metalness={0} />
      </mesh>
      {/* Horizon warm glow */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[15, 130, -50]}>
        <planeGeometry args={[2400, 500]} />
        <meshStandardMaterial color="#fde68a" transparent opacity={0.12} side={THREE.DoubleSide} />
      </mesh>
      {/* Sun disc */}
      <mesh position={[400, 320, -450]}>
        <sphereGeometry args={[26, 16, 16]} />
        <meshStandardMaterial color="#fff8b0" emissive="#ffe066" emissiveIntensity={2.2} roughness={1} metalness={0} />
      </mesh>

      {/* ── Ground layers ── */}
      {/* Main grassland */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[15, -0.5, -47]} receiveShadow>
        <planeGeometry args={[1200, 1200]} />
        <meshStandardMaterial color="#4a8c4e" roughness={0.94} metalness={0} />
      </mesh>
      {/* Canyon section — red rocky ground (around x: 70–230, z: -205 to -160) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[150, -0.48, -185]} receiveShadow>
        <planeGeometry args={[220, 60]} />
        <meshStandardMaterial color="#a0522d" roughness={0.96} />
      </mesh>
      {/* Coastal cliff rocky ground (around x: -85 to -238, z: 72–112) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-160, -0.48, 95]} receiveShadow>
        <planeGeometry args={[200, 55]} />
        <meshStandardMaterial color="#8b7355" roughness={0.97} />
      </mesh>
      {/* Lake / water body (Zone 5: x: -50 to 40, z: -18 to 95) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.44, 42]}>
        <planeGeometry args={[120, 80]} />
        <meshStandardMaterial color="#3b82f6" metalness={0.25} roughness={0.12} transparent opacity={0.80} />
      </mesh>
      {/* Lily pads on lake */}
      {[0,1,2,3,4].map((i) => (
        <mesh key={i} rotation={[-Math.PI/2, 0, i*1.2]} position={[-18+i*9, -0.42, 30+i*8]}>
          <circleGeometry args={[2.5+i*0.4, 16]} />
          <meshStandardMaterial color="#166534" roughness={0.9} />
        </mesh>
      ))}
      {/* Tarmac near the station */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[60, -0.48, 0]} receiveShadow>
        <planeGeometry args={[100, 35]} />
        <meshStandardMaterial color="#374151" roughness={0.88} />
      </mesh>
      {/* Distance Plain — pale tarmac strip (z≈-90, x: -135 to 140) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.47, -90]} receiveShadow>
        <planeGeometry args={[300, 30]} />
        <meshStandardMaterial color="#4b5563" roughness={0.90} />
      </mesh>

      {/* ── Clouds ── */}
      <Cloud pos={[-180, 115, -350]} />
      <Cloud pos={[ 100, 140, -400]} />
      <Cloud pos={[ 350, 110, -320]} />
      <Cloud pos={[-350, 130, -280]} />
      <Cloud pos={[  20, 150, -430]} />
      <Cloud pos={[-100, 105, 200]} />
      <Cloud pos={[ 250, 125, 150]} />

      {/* ── Mountains (far background) ── */}
      {/* North backdrop */}
      <Mountain pos={[-300, 0, -500]} h={220} r={130} />
      <Mountain pos={[  50, 0, -550]} h={260} r={150} />
      <Mountain pos={[ 320, 0, -480]} h={180} r={110} />
      <Mountain pos={[ 520, 0, -420]} h={130} r={80}  />
      <Mountain pos={[-520, 0, -400]} h={150} r={95}  />
      {/* East / west sides */}
      <Mountain pos={[ 520, 0, -100]} h={120} r={80}  snowLine={0.85} />
      <Mountain pos={[-520, 0,  -80]} h={140} r={90}  snowLine={0.85} />
      {/* South / behind station */}
      <Mountain pos={[-200, 0,  300]} h={100} r={70}  snowLine={0.9}  />
      <Mountain pos={[ 200, 0,  280]} h={110} r={75}  snowLine={0.9}  />
      {/* Near mid-range hills */}
      <Mountain pos={[-230, 0, -150]} h={70}  r={55}  snowLine={0.92} />
      <Mountain pos={[ 350, 0, -100]} h={65}  r={48}  snowLine={0.92} />
      <Mountain pos={[  15, 0,  180]} h={55}  r={42}  snowLine={0.95} />

      {/* ── ZONE 1: Launch Valley — dense pine forest both sides ── */}
      {Array.from({ length: 32 }, (_, i) => (
        <Tree key={`z1r${i}`} pos={[i * 8 - 20, 0, 22 + Math.sin(i)*4]} scale={0.9+Math.sin(i)*0.2} hue={i%5} />
      ))}
      {Array.from({ length: 28 }, (_, i) => (
        <RoundTree key={`z1l${i}`} pos={[i * 8 - 10, 0, -22 + Math.cos(i)*3]}
          scale={0.85+Math.cos(i*1.3)*0.15} color={["#2d6b2d","#3a8a3a","#4a9a4a"][i%3]} />
      ))}

      {/* ── ZONE 2: Summit Climb 1 — steel roller-coaster structure ── */}
      {/* Support pylons for the 40m first hill */}
      <Pylon pos={[265, 0, -46]} height={10} />
      <Pylon pos={[278, 0, -60]} height={20} />
      <Pylon pos={[287, 0, -73]} height={32} />
      <Pylon pos={[292, 0, -86]} height={42} />
      {/* Cross-beams */}
      {[10, 20, 30, 38].map((h, i) => (
        <Box key={i} pos={[285, h, -75]} size={[20, 0.4, 0.4]} color="#64748b" metalness={0.5} roughness={0.4} />
      ))}
      <Box pos={[281, 18, -75]} size={[0.35, 22, 0.35]} color="#64748b" metalness={0.5} roughness={0.4} />
      <Box pos={[289, 18, -75]} size={[0.35, 22, 0.35]} color="#64748b" metalness={0.5} roughness={0.4} />
      {/* Descent pylons */}
      <Pylon pos={[286, 0, -103]} height={29} />
      <Pylon pos={[272, 0, -122]} height={14} />
      <Pylon pos={[252, 0, -143]} height={5}  />
      {/* Sparse pines flanking the approach */}
      {Array.from({ length: 14 }, (_, i) => (
        <Tree key={`z2a${i}`} pos={[245+i*4, 0, -40-i*2]} scale={1.0+i*0.04} hue={i%5} />
      ))}

      {/* ── ZONE 3: Canyon Sweep — red rocky walls ── */}
      {/* Canyon walls — tall reddish boxes */}
      {Array.from({ length: 12 }, (_, i) => (
        <Box key={`cw1${i}`} pos={[195-i*12, 0, -210]} size={[10, 18+i%3*6, 8]}
          color={["#8b3a3a","#a0522d","#7c4c3c"][i%3]} roughness={0.97} cast={false} />
      ))}
      {Array.from({ length: 10 }, (_, i) => (
        <Box key={`cw2${i}`} pos={[70+i*12, 0, -212]} size={[10, 14+i%4*4, 8]}
          color={["#a0522d","#8b3a3a","#c1784a"][i%3]} roughness={0.97} cast={false} />
      ))}
      {/* Canyon inner walls near the turn */}
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={`cw3${i}`} pos={[130+i*10, 0, -195+i*5]} size={[8, 20+i*2, 6]}
          color="#7c4c3c" roughness={0.97} cast={false} />
      ))}
      <Barrier from={[195, 1, -188]} to={[150, 1, -208]} />
      <Barrier from={[105, 1, -206]} to={[65, 1, -196]} />
      <Barrier from={[30, 1, -180]}  to={[2, 1, -162]} />

      {/* ── ZONE 4: Highland Ridge — mist-shrouded pines ── */}
      {Array.from({ length: 20 }, (_, i) => (
        <Tree key={`z4a${i}`} pos={[-30-i*6, 0, -145+i*4]} scale={1.1+i*0.03} hue={i%5} />
      ))}
      {Array.from({ length: 16 }, (_, i) => (
        <Tree key={`z4b${i}`} pos={[-12-i*5, 0, -120+i*5]} scale={1.0+i*0.02} hue={(i+2)%5} />
      ))}
      {/* Highland ridge pylons */}
      <Pylon pos={[-50, 0, -119]} height={14} />
      <Pylon pos={[-63, 0, -102]} height={24} />
      <Pylon pos={[-68, 0, -87]}  height={30} />

      {/* ── ZONE 5: Lake Valley — willow-like round trees, water ── */}
      {Array.from({ length: 18 }, (_, i) => (
        <RoundTree key={`z5a${i}`} pos={[45+Math.cos(i/18*Math.PI*2)*50, 0, 45+Math.sin(i/18*Math.PI*2)*35]}
          scale={0.9+i*0.02} color={["#3a7d44","#2d6b2d","#4fa04f","#226622"][i%4]} />
      ))}
      {/* S-bend barriers */}
      <Barrier from={[40, 1, 22]}   to={[28, 1, 52]} />
      <Barrier from={[9, 1, 72]}    to={[-22, 1, 88]} />

      {/* ── ZONE 6: Coastal Cliff — rocky outcrops ── */}
      {/* Cliff pillars */}
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={`cliff${i}`} pos={[-90-i*18, 0, 116]} size={[10, 22+i*3, 12]}
          color={["#8b7355","#9b8165","#7c6445"][i%3]} roughness={0.98} cast={false} />
      ))}
      {/* Coastal palm-style round trees */}
      {Array.from({ length: 10 }, (_, i) => (
        <RoundTree key={`z6p${i}`} pos={[-90-i*15, 0, 68+i*4]} scale={0.8+i*0.03} color="#4a9a4a" />
      ))}
      <Barrier from={[-90, 5, 105]}  to={[-155, 15, 112]} />
      <Barrier from={[-180, 20, 108]} to={[-208, 12, 100]} />

      {/* ── ZONE 7: Jungle Spiral — dense tropical greenery ── */}
      {Array.from({ length: 24 }, (_, i) => (
        <RoundTree key={`z7a${i}`} pos={[-248-i*1.5, 0, 50-i*5+Math.sin(i)*8]}
          scale={1.0+Math.sin(i)*0.25} color={["#1e5c1e","#226622","#2d7a2d","#1a4f1a"][i%4]} />
      ))}
      {Array.from({ length: 18 }, (_, i) => (
        <Tree key={`z7b${i}`} pos={[-240-i*3, 0, -30+Math.cos(i*1.4)*12]} scale={1.1+i*0.02} hue={2} />
      ))}
      <Barrier from={[-228, 4, -40]}  to={[-212, 6, -56]} />
      <Barrier from={[-190, 2, -70]}  to={[-167, 1, -80]} />

      {/* ── ZONE 8: Distance Plain — clean open run, boards both sides ── */}
      {/* Distance marker boards — north side of straight */}
      {Array.from({ length: 14 }, (_, i) => (
        <DistBoard key={`dm${i}`} pos={[-130+i*20, 0, -78]} color={i<10 ? "#22c55e" : "#ef4444"} />
      ))}
      {/* Sparse lampposts along the straight */}
      {Array.from({ length: 10 }, (_, i) => (
        <group key={`lp${i}`} position={[-130+i*28, 0, -100]}>
          <Cyl pos={[0, 4, 0]} args={[0.12, 0.12, 8, 5]} color="#374151" roughness={0.85} />
          <mesh position={[0.6, 7.8, 0]}>
            <sphereGeometry args={[0.5, 8, 8]} />
            <meshStandardMaterial color="#fef9c3" emissive="#fef08a" emissiveIntensity={2.0} />
          </mesh>
        </group>
      ))}

      {/* ── ZONE 9: Summit Climb 2 — second big hill structure ── */}
      <Pylon pos={[175, 0, -80]} height={10} />
      <Pylon pos={[198, 0, -66]} height={20} />
      <Pylon pos={[214, 0, -51]} height={30} />
      <Pylon pos={[226, 0, -38]} height={38} />
      {[10, 20, 28].map((h, i) => (
        <Box key={i} pos={[215, h, -55]} size={[16, 0.4, 0.4]} color="#64748b" metalness={0.5} roughness={0.4} />
      ))}
      <Box pos={[211, 16, -55]} size={[0.35, 18, 0.35]} color="#64748b" metalness={0.5} roughness={0.4} />
      <Box pos={[219, 16, -55]} size={[0.35, 18, 0.35]} color="#64748b" metalness={0.5} roughness={0.4} />
      {/* Trees flanking the second hill */}
      {Array.from({ length: 12 }, (_, i) => (
        <Tree key={`z9t${i}`} pos={[158+i*5, 0, -65-i*2]} scale={1.0+i*0.03} hue={(i+1)%5} />
      ))}

      {/* ── ZONE 10: Finish Valley — welcoming open grass ── */}
      {Array.from({ length: 20 }, (_, i) => (
        <RoundTree key={`z10a${i}`} pos={[140+i*7, 0, 20+Math.sin(i)*6]}
          scale={0.85+i*0.02} color={["#3a7d44","#4fa04f","#2d6b2d"][i%3]} />
      ))}

      {/* ── Buildings (varied across zones) ── */}
      {/* Zone 1 — ticket booths near station */}
      <Building pos={[20, 0, 20]}  size={[5,5,4]}   wallColor="#fde68a" roofColor="#f59e0b" />
      <Building pos={[32, 0, 20]}  size={[5,5,4]}   wallColor="#dbeafe" roofColor="#2563eb" />
      {/* Zone 1 — main research buildings */}
      <Building pos={[50, 0, -60]}  size={[18,20,14]} wallColor="#e2e8f0" roofColor="#dc2626" />
      <Building pos={[80, 0, -55]}  size={[12,14,10]} wallColor="#fde68a" roofColor="#1d4ed8" />
      <Building pos={[-60, 0, -50]} size={[14,16,11]} wallColor="#e8d5b0" roofColor="#c0392b" />
      {/* Zone 3 — canyon outpost */}
      <Building pos={[190, 0, -170]} size={[8,10,7]}  wallColor="#c9a87c" roofColor="#5c3a1e" />
      {/* Zone 6 — coastal observatory */}
      <Building pos={[-220, 0, 60]}  size={[12,14,10]} wallColor="#e0f2fe" roofColor="#0369a1" />
      {/* Zone 8 — distance plain checkpoint */}
      <Building pos={[-10, 0, -108]}  size={[8,8,6]}  wallColor="#f0fdf4" roofColor="#15803d" />
      <Building pos={[-30, 0, -108]}  size={[6,6,5]}  wallColor="#fffbeb" roofColor="#b45309" />

      {/* ── Control Tower ── */}
      <ControlTower pos={[130, 0, -55]} />

      {/* ── Spectator Stands ── */}
      {/* Launch area */}
      <Stand pos={[80, 0, 22]}   rot={[0, 0, 0]} />
      <Stand pos={[140, 0, 22]}  rot={[0, Math.PI/12, 0]} />
      {/* Zone 8 distance plain — viewing gallery */}
      <Stand pos={[60, 0, -108]}  rot={[0, Math.PI/2, 0]} />
      {/* Zone 5 lake side */}
      <Stand pos={[55, 0, 70]}    rot={[0, -Math.PI/6, 0]} />

      {/* ── Finish arch ── */}
      <group position={[10, 0, 1]}>
        {[-6, 6].map((z, i) => (
          <Cyl key={i} pos={[0, 9, z]} args={[0.6, 0.6, 18, 8]} color="#dc2626" metalness={0.2} roughness={0.6} />
        ))}
        <Box pos={[0, 18.2, 0]} size={[0.6, 0.6, 12.6]} color="#dc2626" metalness={0.2} roughness={0.6} />
        {["#ef4444","#f59e0b","#22c55e","#3b82f6","#a855f7"].map((c, i) => (
          <Box key={i} pos={[0.1, 17, -4+i*2]} size={[0.12, 2.2, 1.9]} color={c}
            emissive={c} emissiveIntensity={0.3} />
        ))}
        <mesh rotation={[-Math.PI/2, 0, 0]} position={[0, -0.44, 0]}>
          <planeGeometry args={[3, 14]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        {Array.from({ length: 6 }, (_, i) =>
          Array.from({ length: 3 }, (__, j) => (
            (i + j) % 2 === 0 ? null :
            <mesh key={`${i}${j}`} rotation={[-Math.PI/2, 0, 0]} position={[-0.9+j*0.9, -0.43, -2.2+i*0.9]}>
              <planeGeometry args={[0.88, 0.88]} />
              <meshStandardMaterial color="#222" />
            </mesh>
          ))
        )}
      </group>

      {/* ── Fog ── */}
      <fog attach="fog" args={["#c8e6f5", 300, 800]} />
    </group>
  );
}
